import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Pencil, Plus, FileText } from "lucide-react";
import Sidebar from "../../../component/includes/sidebar";
import {
  FooterContent1,
  UpdateFooterContent1Data,
  footerContent1Service,
} from "../../../services/footer/alamat";

const FooterContent1Management: React.FC = () => {
  const [footerContent, setFooterContent] = useState<FooterContent1[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFooterContent, setEditingFooterContent] = useState<FooterContent1 | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const fetchFooterContent = async () => {
    try {
      setLoading(true);
      const response = await footerContent1Service.getFooterContent1();
      setFooterContent(response || []);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to fetch footer content");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFooterContent();
  }, []);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 3000);
  };

  const resetForm = () => {
    setEditingFooterContent(null);
    setIsModalOpen(false);
    setImagePreview(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && editingFooterContent) {
      setEditingFooterContent({ ...editingFooterContent, image: file });

      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const filteredFooterContent = footerContent.filter((content) =>
    content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUpdateFooterContent = async () => {
    if (!editingFooterContent) return;

    if (!editingFooterContent.title.trim() || !editingFooterContent.description.trim()) {
      showError("Title and description cannot be empty");
      return;
    }

    try {
      const updatedData: UpdateFooterContent1Data = {
        id: editingFooterContent.id,
        title: editingFooterContent.title,
        description: editingFooterContent.description,
        image: editingFooterContent.image,
      };

      await footerContent1Service.updateFooterContent1(updatedData);
      await fetchFooterContent();
      showSuccess("Footer content updated successfully");
      resetForm();
    } catch (err) {
      showError("Failed to update footer content");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 flex flex-col">
<div className="flex-1 p-4 md:p-6 mt-14">
          <div className="max-w-7xl mx-auto">
            {/* Header seperti Article Management */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <FileText size={32} className="text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Footer Content Management</h1>
                  <p className="text-gray-500 text-sm mt-1">Manage your footer section content</p>
                </div>
              </div>

              {/* Tombol Add (bisa dikembangkan nanti jika ada fitur create) */}
              {/* Saat ini hanya Edit, jadi tombol ini bisa di-comment atau di-hide */}
              {/* 
              <button
                onClick={() => {
                  resetForm();
                  setIsModalOpen(true);
                }}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-medium transition-all active:scale-95"
              >
                <Plus className="w-5 h-5" />
                Add New Footer Content
              </button>
              */}
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search footer content by title or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-2xl py-4 pl-12 pr-6 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                />
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">🔍</div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-20">ID</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">IMAGE</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">TITLE</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">DESCRIPTION</th>
                    <th className="px-8 py-5 text-right font-medium text-gray-600 w-32">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredFooterContent.map((content, index) => (
                    <tr key={content.id} className="hover:bg-gray-50 transition-colors">
                      {/* ID dengan nomor urut */}
                      <td className="px-8 py-6">
                        <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-lg">
                          {index + 1}
                        </div>
                      </td>

                      {/* Image */}
                      <td className="px-8 py-6">
                        <img
                          src={`${process.env.REACT_APP_API_URL}${content.image}`}
                          alt={content.title}
                          className="w-16 h-16 object-contain rounded-lg border border-gray-200"
                        />
                      </td>

                      {/* Title */}
                      <td className="px-8 py-6 font-medium text-gray-800">
                        {content.title}
                      </td>

                      {/* Description */}
                      <td className="px-8 py-6 text-gray-700 max-w-md">
                        {content.description.length > 150
                          ? content.description.substring(0, 150) + "..."
                          : content.description}
                      </td>

                      {/* Actions */}
                      <td className="px-8 py-6">
                        <div className="flex items-center justify-end">
                          <button
                            onClick={() => {
                              setEditingFooterContent(content);
                              setImagePreview(`${process.env.REACT_APP_API_URL}${content.image}`);
                              setIsModalOpen(true);
                            }}
                            className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all hover:scale-110"
                            title="Edit"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {filteredFooterContent.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-20 text-center text-gray-400">
                        No footer content found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal - Dipercantik seperti Article */}
      {isModalOpen && editingFooterContent && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-semibold mb-6">Edit Footer Content</h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={editingFooterContent.title}
                  onChange={(e) => setEditingFooterContent({ ...editingFooterContent, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <ReactQuill
                  value={editingFooterContent.description}
                  onChange={(value) => setEditingFooterContent({ ...editingFooterContent, description: value })}
                  className="h-64"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full border border-gray-300 rounded-2xl px-4 py-3"
                />
                {imagePreview && (
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="mt-4 w-40 h-40 object-contain rounded-lg border" 
                  />
                )}
              </div>
            </div>

            <div className="flex gap-4 mt-10">
              <button
                onClick={resetForm}
                className="flex-1 py-4 border border-gray-300 rounded-2xl font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateFooterContent}
                className="flex-1 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-medium transition-all"
              >
                Update Footer Content
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FooterContent1Management;